import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { desktopVersionsApi } from '../services/api';
import {
  DesktopDepotFichier,
  DesktopLotEtat,
  DesktopMiseAJour,
  DesktopProduit,
  DesktopTelechargement,
  DesktopVersion,
  DesktopVersionInput,
} from '../types';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../components/Toast';
import {
  changementsSaisie,
  compareVersions,
  DESKTOP_PRODUITS,
  derniereApresCorrection,
  ErreurAppel,
  erreurNotes,
  findSameNumber,
  formatDateCourte,
  formatTaille,
  latestPublished,
  lireErreur,
  messageErreur,
  nomProduit,
  NOTES_MAX,
  parseVersion,
  resumeLot,
  VersionSaisie,
} from '../utils/desktopVersions';
import './Pages.css';
import './DesktopVersions.css';

// Page Gestion des versions : la procédure de docs/publier-version-poste.md sans SQL.
// Inscrire (invisible des postes), vérifier ce que voient les postes, publier, dépublier,
// corriger (une version publiée : vue des postes dès l'enregistrement, après confirmation),
// supprimer une version non publiée. Le serveur refait tous les contrôles.
//
// Palier 2 — le lot : l'éditeur DÉPOSE son fichier sur le serveur (scp), puis on le
// RATTACHE ici à une version. La taille et le SHA-256 sont calculés par le serveur sur le
// fichier déposé, jamais saisis : ils s'affichent pour être comparés au publication.txt.

type VersionForm = VersionSaisie;

interface ModalState {
  mode: 'create' | 'edit';
  id?: number;
  publiee: boolean;
  initial: VersionForm;
  form: VersionForm;
  // Échec du dernier enregistrement : reste affiché dans la fenêtre jusqu'à la saisie suivante
  erreur?: ErreurAppel;
}

// Fenêtre « Lot » : ce que le dépôt du produit contient, et ce qu'on y choisit
interface LotModalState {
  item: DesktopVersion;
  chargement: boolean;
  lots: DesktopDepotFichier[];
  porteurs: DesktopDepotFichier[];
  choixLot: string;
  choixPorteur: string;
}

// Fenêtre « Nettoyer » : ce que le serveur supprimerait (aperçu), puis la confirmation
interface NettoyageVersion {
  id: number;
  produit: string;
  version: string;
  publiee: boolean;
  lot_fichier: string | null;
  porteur_fichier: string | null;
}

interface NettoyageFichier {
  produit: string;
  nom: string;
  taille_octets: number;
  depose_le: string;
  raison: string;
}

interface NettoyageState {
  produit: string;              // '' : tous les produits
  chargement: boolean;
  versions: NettoyageVersion[];
  fichiers: NettoyageFichier[];
  octets: number;
}

const EMPTY_FORM: VersionForm = {
  produit: 'SCF',
  version: '',
  publiee_le: '',
  taille_octets: '',
  obligatoire: false,
  notes: '',
};

// 404 ou 409 : la liste affichée ne correspond plus à la base (modifiée depuis un autre poste).
const listePerimee = (err: any): boolean => [404, 409].includes(err?.response?.status);

const DownloadIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" className="dv-icone">
    <path d="M12 4v11" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    <path d="m7.5 10.5 4.5 4.5 4.5-4.5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M5 19.5h14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const DesktopVersions: React.FC = () => {
  const [items, setItems] = useState<DesktopVersion[]>([]);
  const [loading, setLoading] = useState(false);
  const [filtreProduit, setFiltreProduit] = useState<'all' | DesktopProduit>('all');
  const [filtreEtat, setFiltreEtat] = useState<'all' | 'publiee' | 'inscrite'>('all');
  const [modal, setModal] = useState<ModalState | null>(null);
  const [lotModal, setLotModal] = useState<LotModalState | null>(null);
  const [nettoyage, setNettoyage] = useState<NettoyageState | null>(null);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [apercuProduit, setApercuProduit] = useState<DesktopProduit>('SCF');
  const [apercuVersion, setApercuVersion] = useState('');
  const [apercuLoading, setApercuLoading] = useState(false);
  const [apercu, setApercu] = useState<{
    produit: DesktopProduit;
    version: string;
    miseAJour: DesktopMiseAJour;
    telechargement: DesktopTelechargement | null;
    lotEtat: DesktopLotEtat;
  } | null>(null);
  const { hasPermission } = useAuth();
  const { showToast, showConfirm } = useToast();

  const canCreate = hasPermission('desktop_versions', 'create');
  const canEdit = hasPermission('desktop_versions', 'edit');
  const canPublish = hasPermission('desktop_versions', 'publish');
  const canDelete = hasPermission('desktop_versions', 'delete');

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await desktopVersionsApi.getAll();
      const data = response.data?.items;
      setItems(Array.isArray(data) ? data : []);
    } catch (err: any) {
      showToast(messageErreur(err, 'Chargement des versions impossible'), 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const inscritesParProduit = useMemo(() => {
    const compte: Record<string, number> = {};
    items.forEach((item) => {
      if (!item.publiee) {
        compte[item.produit] = (compte[item.produit] || 0) + 1;
      }
    });
    return compte;
  }, [items]);

  const filteredItems = useMemo(
    () =>
      items.filter(
        (item) =>
          (filtreProduit === 'all' || item.produit === filtreProduit) &&
          (filtreEtat === 'all' || (filtreEtat === 'publiee' ? item.publiee : !item.publiee))
      ),
    [items, filtreProduit, filtreEtat]
  );

  // ---- Formulaire : contrôles de saisie (mêmes règles que le serveur) --------------------
  const formCheck = useMemo(() => {
    if (!modal) {
      return { valid: false, version: '', doublon: '', date: '', taille: '', notes: '', changements: [] as string[] };
    }
    const { form } = modal;
    const texte = form.version.trim();
    const parts = parseVersion(texte);
    let version = '';
    let doublon = '';
    if (texte === '') {
      version = 'Version obligatoire';
    } else if (!parts) {
      version = 'Quatre entiers de 0 à 65535 séparés par des points (ex. 2026.10.02.1 ou 1.6.0.2)';
    } else {
      const meme = findSameNumber(items, form.produit, parts, modal.id);
      if (meme) {
        doublon = `${nomProduit(form.produit)} ${meme.version} existe déjà : c'est le même numéro.`;
      }
    }
    const date = form.publiee_le !== '' && !/^\d{4}-\d{2}-\d{2}$/.test(form.publiee_le) ? 'Date invalide' : '';
    const taille = form.taille_octets.trim() !== '' && !/^\d{1,15}$/.test(form.taille_octets.trim())
      ? "Nombre entier d'octets"
      : '';
    const notes = erreurNotes(form.notes);
    // Correction : rien à enregistrer tant qu'aucun champ ne change
    const changements = modal.mode === 'edit' ? changementsSaisie(modal.initial, form) : [];
    const inchange = modal.mode === 'edit' && changements.length === 0;
    return {
      valid: !version && !doublon && !date && !taille && !notes && !inchange,
      version, doublon, date, taille, notes, changements,
    };
  }, [modal, items]);

  const updateForm = (patch: Partial<VersionForm>) =>
    setModal((current) =>
      current ? { ...current, erreur: undefined, form: { ...current.form, ...patch } } : current);

  const openCreate = () => {
    const produit = filtreProduit === 'all' ? 'SCF' : filtreProduit;
    const form = { ...EMPTY_FORM, produit };
    setModal({ mode: 'create', publiee: false, initial: form, form });
  };

  const openEdit = (item: DesktopVersion) => {
    const form: VersionForm = {
      produit: item.produit,
      version: item.version,
      publiee_le: item.publiee_le || '',
      taille_octets: item.taille_octets === null ? '' : String(item.taille_octets),
      obligatoire: item.obligatoire,
      notes: item.notes || '',
    };
    setModal({ mode: 'edit', id: item.id, publiee: item.publiee, initial: form, form });
  };

  // Corriger une version publiée : ce qui change, puis ce que verront les postes
  const messageCorrectionPubliee = (etat: ModalState, changements: string[]): string => {
    const { initial, form } = etat;
    const id = etat.id as number;
    const phrases = [`Corriger ${nomProduit(initial.produit)} ${initial.version}, publiée ?`, `${changements.join(' ; ')}.`];
    const avantParts = parseVersion(initial.version.trim());
    const apresParts = parseVersion(form.version.trim());
    const memeNumero = initial.produit === form.produit && avantParts !== null && apresParts !== null &&
      compareVersions(avantParts, apresParts) === 0;
    if (memeNumero) {
      const actuelle = latestPublished(items, form.produit);
      phrases.push(!actuelle || actuelle.id === id
        ? `Les postes ${nomProduit(form.produit)} verront la correction dès l'enregistrement.`
        : `Ce n'est pas la version que voient les postes : ${actuelle.version} reste la dernière publiée.`);
    } else {
      if (initial.produit !== form.produit) {
        const restante = latestPublished(items, initial.produit, id);
        phrases.push(restante
          ? `${nomProduit(initial.produit)} : ${restante.version} devient la dernière version publiée.`
          : `${nomProduit(initial.produit)} : plus aucune version publiée, les postes recevront « aucune mise à jour ».`);
      }
      const nouvelle = derniereApresCorrection(items, id, form.produit, form.version.trim());
      phrases.push(`${nomProduit(form.produit)} : la dernière version publiée sera ${nouvelle ? nouvelle.version : form.version.trim()}.`);
    }
    return phrases.join(' ');
  };

  const enregistrer = async (etat: ModalState) => {
    const { form } = etat;
    const payload: DesktopVersionInput = {
      produit: form.produit,
      version: form.version.trim(),
      publiee_le: form.publiee_le || null,
      taille_octets: form.taille_octets.trim() === '' ? null : Number(form.taille_octets.trim()),
      obligatoire: form.obligatoire,
      notes: form.notes.trim() === '' ? null : form.notes,
    };
    const libelle = `${nomProduit(form.produit)} ${payload.version}`;
    try {
      setSaving(true);
      setModal((current) => (current ? { ...current, erreur: undefined } : current));
      if (etat.mode === 'create') {
        await desktopVersionsApi.create(payload);
        showToast(`${libelle} inscrite, non publiée`, 'success');
      } else {
        await desktopVersionsApi.update(etat.id as number, payload);
        showToast(`${libelle} corrigée`, 'success');
      }
      setModal(null);
      await loadData();
    } catch (err: any) {
      // La cause reste dans la fenêtre : un toast de quatre secondes ne laisse pas la lire
      const erreur = lireErreur(err, 'Enregistrement impossible');
      showToast(erreur.message, 'error');
      setModal((current) => (current ? { ...current, erreur } : current));
      if (listePerimee(err)) {
        await loadData();
      }
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!modal || !formCheck.valid) {
      return;
    }
    if (modal.mode === 'edit' && modal.publiee) {
      const etat = modal;
      showConfirm(
        messageCorrectionPubliee(etat, formCheck.changements),
        () => { void enregistrer(etat); },
        'Corriger une version publiée'
      );
      return;
    }
    void enregistrer(modal);
  };

  // ---- Actions sur une ligne ------------------------------------------------------------
  const runAction = async (item: DesktopVersion, action: () => Promise<unknown>, succes: string) => {
    try {
      setBusyId(item.id);
      await action();
      showToast(`${nomProduit(item.produit)} ${item.version} ${succes}`, 'success');
      await loadData();
    } catch (err: any) {
      showToast(messageErreur(err, 'Action impossible'), 'error');
      if (listePerimee(err)) {
        await loadData();
      }
    } finally {
      setBusyId(null);
    }
  };

  const handlePublish = (item: DesktopVersion) => {
    const nom = nomProduit(item.produit);
    const actuelle = latestPublished(items, item.produit);
    const parts = parseVersion(item.version);
    const actuelleParts = actuelle ? parseVersion(actuelle.version) : null;
    let consequence: string;
    if (!actuelle || !parts || !actuelleParts || compareVersions(parts, actuelleParts) > 0) {
      consequence = `Tous les postes ${nom} en version inférieure verront la mise à jour ${item.version}` +
        (item.obligatoire ? ', marquée obligatoire.' : '.');
    } else {
      consequence = `${actuelle.version} reste la dernière version publiée : les postes continueront de voir ${actuelle.version}.`;
    }
    showConfirm(
      `Publier ${nom} ${item.version} ? ${consequence}`,
      () => runAction(item, () => desktopVersionsApi.publish(item.id), 'publiée'),
      'Publier la version'
    );
  };

  const handleUnpublish = (item: DesktopVersion) => {
    const nom = nomProduit(item.produit);
    const suivante = latestPublished(items, item.produit, item.id);
    const consequence = item.derniere_publiee
      ? suivante
        ? `Les postes ne verront plus ${item.version} : ${suivante.version} redevient la dernière version publiée.`
        : `Plus aucune version ${nom} ne sera publiée : les postes recevront « aucune mise à jour ».`
      : `Ce n'est pas la dernière version publiée : ce que voient les postes ne change pas.`;
    showConfirm(
      `Dépublier ${nom} ${item.version} ? ${consequence}`,
      () => runAction(item, () => desktopVersionsApi.unpublish(item.id), 'dépubliée'),
      'Dépublier la version'
    );
  };

  const handleDelete = (item: DesktopVersion) => {
    showConfirm(
      `Supprimer définitivement ${nomProduit(item.produit)} ${item.version} ? Elle n'est pas publiée : aucun poste ne la voit.`,
      () => runAction(item, () => desktopVersionsApi.remove(item.id), 'supprimée'),
      'Supprimer la version'
    );
  };

  // ---- Nettoyage -------------------------------------------------------------------------
  // Le serveur calcule ce qui partirait (versions que les postes ne voient plus, et leurs
  // fichiers du dépôt) ; rien n'est supprimé avant la confirmation, et le serveur recalcule
  // au moment de supprimer.
  const chargerNettoyage = async (produit: string) => {
    setNettoyage({ produit, chargement: true, versions: [], fichiers: [], octets: 0 });
    try {
      const response = await desktopVersionsApi.apercuNettoyage(produit);
      setNettoyage({
        produit,
        chargement: false,
        versions: Array.isArray(response.data?.versions) ? response.data.versions : [],
        fichiers: Array.isArray(response.data?.fichiers) ? response.data.fichiers : [],
        octets: Number(response.data?.octets) || 0,
      });
    } catch (err: any) {
      showToast(messageErreur(err, 'Aperçu du nettoyage impossible'), 'error');
      setNettoyage(null);
    }
  };

  const executerNettoyage = (etat: NettoyageState) => {
    const portee = etat.produit ? nomProduit(etat.produit) : 'tous les produits';
    showConfirm(
      `Supprimer définitivement ${etat.versions.length} version(s) et ${etat.fichiers.length} fichier(s) ` +
        `du dépôt (${formatTaille(etat.octets)}) pour ${portee} ? Aucun poste ne voit ces versions, ` +
        `mais « Dépublier » ne pourra plus faire revenir l'une d'elles.`,
      async () => {
        try {
          setSaving(true);
          const response = await desktopVersionsApi.nettoyer(etat.produit);
          const nonEffaces: string[] = response.data?.fichiers_non_effaces || [];
          showToast(response.data?.message || 'Nettoyage effectué', nonEffaces.length ? 'error' : 'success');
          if (nonEffaces.length) {
            showToast(`Fichiers non effacés : ${nonEffaces.join(', ')}`, 'error');
          }
          setNettoyage(null);
          await loadData();
        } catch (err: any) {
          showToast(messageErreur(err, 'Nettoyage impossible'), 'error');
        } finally {
          setSaving(false);
        }
      },
      'Nettoyer'
    );
  };

  // ---- Le lot (palier 2) ----------------------------------------------------------------
  // Le fichier n'est pas téléversé ici : l'éditeur le dépose sur le serveur (scp), on le
  // CHOISIT dans le dépôt du produit. Taille et empreinte sont calculées par le serveur.
  const openLot = async (item: DesktopVersion) => {
    setLotModal({
      item,
      chargement: true,
      lots: [],
      porteurs: [],
      choixLot: item.lot_fichier || '',
      choixPorteur: item.porteur_fichier || '',
    });
    try {
      const response = await desktopVersionsApi.depot(item.produit);
      setLotModal((current) =>
        current && current.item.id === item.id
          ? {
              ...current,
              chargement: false,
              lots: Array.isArray(response.data?.lots) ? response.data.lots : [],
              porteurs: Array.isArray(response.data?.porteurs) ? response.data.porteurs : [],
            }
          : current
      );
    } catch (err: any) {
      setLotModal(null);
      showToast(messageErreur(err, 'Lecture du dépôt impossible'), 'error');
    }
  };

  const rattacherLot = async (etat: LotModalState) => {
    const { item, choixLot, choixPorteur } = etat;
    try {
      setSaving(true);
      const response = await desktopVersionsApi.attacherLot(item.id, {
        lot_fichier: choixLot,
        porteur_fichier: choixPorteur === '' ? null : choixPorteur,
      });
      const ligne: DesktopVersion | undefined = response.data?.item;
      showToast(
        `Lot rattaché à ${nomProduit(item.produit)} ${item.version}` +
          (ligne?.lot_taille_octets ? ` — ${formatTaille(ligne.lot_taille_octets)}` : ''),
        'success'
      );
      setLotModal(null);
      await loadData();
    } catch (err: any) {
      showToast(messageErreur(err, 'Rattachement impossible'), 'error');
      if (listePerimee(err)) {
        await loadData();
      }
    } finally {
      setSaving(false);
    }
  };

  const detacherLot = (item: DesktopVersion) => {
    const suite = item.publiee
      ? `Les postes ${nomProduit(item.produit)} ne recevront plus d'adresse de téléchargement : ils sauront qu'une version existe, sans pouvoir la prendre ici.`
      : `Cette version n'est pas publiée : aucun poste n'est concerné.`;
    showConfirm(
      `Détacher le lot de ${nomProduit(item.produit)} ${item.version} ? ${suite} Le fichier reste dans le dépôt.`,
      async () => {
        try {
          setSaving(true);
          await desktopVersionsApi.detacherLot(item.id);
          showToast(`Lot détaché ; le fichier reste dans le dépôt`, 'success');
          setLotModal(null);
          await loadData();
        } catch (err: any) {
          showToast(messageErreur(err, 'Détachement impossible'), 'error');
          if (listePerimee(err)) {
            await loadData();
          }
        } finally {
          setSaving(false);
        }
      },
      'Détacher le lot'
    );
  };

  // ---- Vérifier un poste ----------------------------------------------------------------
  const apercuVersionValide = parseVersion(apercuVersion.trim()) !== null;

  const handleApercu = async (event: React.FormEvent) => {
    event.preventDefault();
    const version = apercuVersion.trim();
    if (!parseVersion(version)) {
      return;
    }
    try {
      setApercuLoading(true);
      const response = await desktopVersionsApi.apercu(apercuProduit, version);
      setApercu({
        produit: apercuProduit,
        version,
        miseAJour: response.data?.mise_a_jour,
        // Ce que recevrait un poste COUVERT : le droit ne vient pas du catalogue
        telechargement: response.data?.telechargement ?? null,
        lotEtat: (response.data?.lot_etat as DesktopLotEtat) ?? 'non',
      });
    } catch (err: any) {
      setApercu(null);
      showToast(messageErreur(err, 'Vérification impossible'), 'error');
    } finally {
      setApercuLoading(false);
    }
  };

  const resumeApercu = (resultat: { produit: DesktopProduit; version: string; miseAJour: DesktopMiseAJour }) => {
    const { miseAJour } = resultat;
    if (!miseAJour.derniere_version) {
      return `Aucune version ${nomProduit(resultat.produit)} publiée : le poste reçoit « aucune mise à jour ».`;
    }
    if (miseAJour.disponible) {
      return `Mise à jour disponible : ${miseAJour.derniere_version}` +
        (miseAJour.obligatoire ? ' (obligatoire)' : '') + '.';
    }
    return `À jour : la dernière version publiée est ${miseAJour.derniere_version}.`;
  };

  return (
    <div className="page-container desktop-versions">
      <div className="page-header">
        <div>
          <h1>Gestion des versions</h1>
          <p className="dv-subtitle">
            Mises à jour des applications de bureau : inscrire, vérifier, publier.
          </p>
        </div>
        <div className="header-actions">
          <button className="btn btn-secondary" onClick={loadData} disabled={loading}>
            Actualiser
          </button>
          {canDelete && (
            <button className="btn btn-secondary" onClick={() => { void chargerNettoyage(''); }} disabled={loading}>
              Nettoyer…
            </button>
          )}
          {canCreate && (
            <button className="btn btn-primary" onClick={openCreate}>
              Inscrire une version
            </button>
          )}
        </div>
      </div>

      <section className="dv-section" aria-labelledby="dv-postes-titre">
        <h2 id="dv-postes-titre" className="dv-section-title">Ce que voient les postes</h2>
        <div className="dv-cards">
          {DESKTOP_PRODUITS.map((produit) => {
            const derniere = items.find((item) => item.produit === produit.code && item.derniere_publiee);
            const inscrites = inscritesParProduit[produit.code] || 0;
            return (
              <article key={produit.code} className={`dv-card ${derniere ? '' : 'dv-card-vide'}`}>
                <header className="dv-card-header">
                  <span className="dv-card-produit">{produit.nom}</span>
                  {inscrites > 0 && (
                    <span className="dv-chip" title="Versions inscrites, invisibles des postes">
                      {inscrites} non publiée{inscrites > 1 ? 's' : ''}
                    </span>
                  )}
                </header>
                {derniere ? (
                  <>
                    <div className="dv-card-version">{derniere.version}</div>
                    <div className="dv-card-meta">
                      <span>Publiée le {formatDateCourte(derniere.publiee_le)}</span>
                      <span>{formatTaille(derniere.taille_octets)}</span>
                      {derniere.obligatoire && <span className="dv-tag dv-tag-obligatoire">Obligatoire</span>}
                    </div>
                    {derniere.notes && <p className="dv-card-notes">{derniere.notes}</p>}
                    {/* Palier 2 : la dernière version publiée porte-t-elle un lot ? */}
                    {(() => {
                      const lot = resumeLot(derniere);
                      if (lot.etat === 'present') {
                        return (
                          <p className="dv-card-lot">
                            {/* /maj/ est servi par nginx sans authentification : le lien suffit */}
                            {derniere.lot_url ? (
                              <a
                                className="dv-card-lot-lien"
                                href={derniere.lot_url}
                                download
                                title={`Télécharger ${derniere.lot_fichier}`}
                              >
                                <DownloadIcon />
                                Lot à télécharger : <strong>{lot.texte}</strong>
                              </a>
                            ) : (
                              <>Lot à télécharger : <strong>{lot.texte}</strong></>
                            )}
                            <span className="dv-field-hint"> — aux postes dont le contrat est en cours</span>
                          </p>
                        );
                      }
                      if (lot.etat === 'absent') {
                        return (
                          <p className="dv-card-lot dv-card-lot-absent">
                            Lot rattaché mais <strong>fichier absent du dépôt</strong> : les postes ne
                            reçoivent aucune adresse.
                          </p>
                        );
                      }
                      return (
                        <p className="dv-card-lot dv-card-lot-aucun">
                          Aucun lot : les postes savent qu'elle existe, sans rien télécharger.
                        </p>
                      );
                    })()}
                  </>
                ) : (
                  <p className="dv-card-empty">
                    Aucune version publiée : les postes reçoivent « aucune mise à jour ».
                  </p>
                )}
              </article>
            );
          })}
        </div>
      </section>

      <section className="dv-section dv-apercu" aria-labelledby="dv-apercu-titre">
        <h2 id="dv-apercu-titre" className="dv-section-title">Vérifier un poste</h2>
        <p className="dv-help">
          La réponse exacte que reçoit un poste, selon son produit et sa version installée.
        </p>
        <form className="dv-apercu-form" onSubmit={handleApercu}>
          <label className="dv-visually-hidden" htmlFor="dv-apercu-produit">Produit</label>
          <select
            id="dv-apercu-produit"
            value={apercuProduit}
            onChange={(event) => setApercuProduit(event.target.value as DesktopProduit)}
          >
            {DESKTOP_PRODUITS.map((produit) => (
              <option key={produit.code} value={produit.code}>{produit.nom}</option>
            ))}
          </select>
          <label className="dv-visually-hidden" htmlFor="dv-apercu-version">Version installée</label>
          <input
            id="dv-apercu-version"
            className="search-input dv-mono"
            placeholder="Version installée, ex. 1.6.0.1"
            value={apercuVersion}
            onChange={(event) => setApercuVersion(event.target.value)}
            aria-invalid={apercuVersion.trim() !== '' && !apercuVersionValide}
          />
          <button className="btn btn-primary" type="submit" disabled={!apercuVersionValide || apercuLoading}>
            {apercuLoading ? 'Vérification…' : 'Vérifier'}
          </button>
        </form>
        {apercuVersion.trim() !== '' && !apercuVersionValide && (
          <p className="dv-field-error">Quatre entiers de 0 à 65535 séparés par des points.</p>
        )}
        {apercu && (
          <div className={`dv-apercu-result ${apercu.miseAJour.disponible ? 'dv-apercu-disponible' : ''}`}>
            <p className="dv-apercu-resume">
              {nomProduit(apercu.produit)} en {apercu.version} — {resumeApercu(apercu)}
            </p>
            {/* Le droit ne vient pas du catalogue : il dépend de la licence et du contrat
                du client. L'objet ci-dessous est celui d'un poste COUVERT. */}
            {apercu.telechargement ? (
              <p className="dv-apercu-lot">
                Un poste <strong>dont le contrat est en cours</strong> reçoit en plus l'adresse du
                lot ({formatTaille(apercu.telechargement.taille_octets)}). Un poste non couvert ne
                la reçoit pas : sa réponse s'arrête à « mise_a_jour » et « droit ».
              </p>
            ) : (
              <p className="dv-apercu-lot dv-apercu-lot-absent">
                {apercu.lotEtat === 'fichier_absent'
                  ? "Un lot est rattaché, mais son fichier manque du dépôt (ou a changé de taille) : aucun poste ne reçoit d'adresse."
                  : "Aucune adresse de téléchargement : ce poste n'a rien à prendre ici, même avec un contrat en cours."}
              </p>
            )}
            <details>
              <summary>Réponse envoyée au poste</summary>
              <pre>
                {JSON.stringify(
                  apercu.telechargement
                    ? { mise_a_jour: apercu.miseAJour, telechargement: apercu.telechargement }
                    : { mise_a_jour: apercu.miseAJour },
                  null,
                  2
                )}
              </pre>
            </details>
          </div>
        )}
      </section>

      <div className="filters">
        <select
          aria-label="Filtrer par produit"
          value={filtreProduit}
          onChange={(event) => setFiltreProduit(event.target.value as 'all' | DesktopProduit)}
        >
          <option value="all">Tous les produits</option>
          {DESKTOP_PRODUITS.map((produit) => (
            <option key={produit.code} value={produit.code}>{produit.nom}</option>
          ))}
        </select>
        <select
          aria-label="Filtrer par état"
          value={filtreEtat}
          onChange={(event) => setFiltreEtat(event.target.value as 'all' | 'publiee' | 'inscrite')}
        >
          <option value="all">Tous les états</option>
          <option value="publiee">Publiées</option>
          <option value="inscrite">Inscrites, non publiées</option>
        </select>
      </div>

      <div className="table-container">
        <table className="dv-table">
          <thead>
            <tr>
              <th>Produit</th>
              <th>Version</th>
              <th>État</th>
              <th>Publiée le</th>
              <th>Taille</th>
              <th>Lot</th>
              <th>Obligatoire</th>
              <th>Notes</th>
              <th>Inscrite le</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={10} className="empty">Chargement...</td>
              </tr>
            )}
            {!loading && filteredItems.length === 0 && (
              <tr>
                <td colSpan={10} className="empty">
                  {items.length === 0
                    ? 'Aucune version inscrite. « Inscrire une version » pour commencer.'
                    : 'Aucune version pour ces filtres.'}
                </td>
              </tr>
            )}
            {!loading && filteredItems.map((item) => (
              <tr key={item.id} className={item.publiee ? '' : 'dv-row-inscrite'}>
                <td>{nomProduit(item.produit)}</td>
                <td className="dv-mono">{item.version}</td>
                <td>
                  {item.publiee ? (
                    <span className="status-badge active">Publiée</span>
                  ) : (
                    <span className="status-badge dv-badge-inscrite">Inscrite</span>
                  )}
                  {item.derniere_publiee && <span className="dv-tag dv-tag-derniere">Vue des postes</span>}
                </td>
                <td>{formatDateCourte(item.publiee_le)}</td>
                <td title={item.taille_octets === null ? '' : `${item.taille_octets.toLocaleString('fr-FR')} octets`}>
                  {formatTaille(item.taille_octets)}
                </td>
                <td title={item.lot_fichier || ''}>
                  {(() => {
                    const lot = resumeLot(item);
                    if (lot.etat === 'aucun') {
                      return <span className="dv-lot-aucun">—</span>;
                    }
                    return (
                      <span className={lot.etat === 'absent' ? 'dv-lot-absent' : 'dv-lot-present'}>
                        {lot.texte}
                      </span>
                    );
                  })()}
                </td>
                <td>{item.obligatoire ? 'Oui' : 'Non'}</td>
                <td className="dv-notes-cell" title={item.notes || ''}>{item.notes || '-'}</td>
                <td>{item.cree_le}</td>
                <td>
                  <div className="dv-actions">
                    {!item.publiee && canPublish && (
                      <button className="btn btn-success" disabled={busyId === item.id} onClick={() => handlePublish(item)}>
                        Publier
                      </button>
                    )}
                    {item.publiee && canPublish && (
                      <button className="btn btn-warning" disabled={busyId === item.id} onClick={() => handleUnpublish(item)}>
                        Dépublier
                      </button>
                    )}
                    {canEdit && (!item.publiee || canPublish) && (
                      <button className="btn btn-secondary" disabled={busyId === item.id} onClick={() => openEdit(item)}>
                        Modifier
                      </button>
                    )}
                    {canEdit && (!item.publiee || canPublish) && (
                      <button
                        className="btn btn-secondary"
                        disabled={busyId === item.id}
                        onClick={() => { void openLot(item); }}
                      >
                        Lot…
                      </button>
                    )}
                    {!item.publiee && canDelete && (
                      <button className="btn btn-secondary dv-btn-danger" disabled={busyId === item.id} onClick={() => handleDelete(item)}>
                        Supprimer
                      </button>
                    )}
                    {!canPublish && !(canEdit && !item.publiee) && !(canDelete && !item.publiee) && '-'}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modal && (
        <div className="modal-overlay" role="presentation">
          <div className="modal" role="dialog" aria-modal="true" aria-labelledby="dv-modal-titre">
            <h2 id="dv-modal-titre">
              {modal.mode === 'create'
                ? 'Inscrire une version'
                : modal.publiee ? 'Corriger une version publiée' : 'Corriger une version non publiée'}
            </h2>
            {modal.mode === 'create' && (
              <p className="dv-info">
                La version est inscrite <strong>sans être publiée</strong> : aucun poste ne la voit avant « Publier ».
              </p>
            )}
            {modal.mode === 'edit' && modal.publiee && (
              <p className="dv-info dv-info-publiee">
                Cette version est <strong>publiée</strong> : la correction s'applique aux postes dès l'enregistrement.
                Les changements vous seront récapitulés avant.
              </p>
            )}
            <form onSubmit={handleSubmit} noValidate>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="dv-produit">Produit</label>
                  <select
                    id="dv-produit"
                    value={modal.form.produit}
                    onChange={(event) => updateForm({ produit: event.target.value as DesktopProduit })}
                  >
                    {DESKTOP_PRODUITS.map((produit) => (
                      <option key={produit.code} value={produit.code}>{produit.nom}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="dv-version">Version</label>
                  <input
                    id="dv-version"
                    className="dv-mono"
                    placeholder="ex. 2026.10.02.1"
                    value={modal.form.version}
                    onChange={(event) => updateForm({ version: event.target.value })}
                    aria-invalid={Boolean(formCheck.version || formCheck.doublon) && modal.form.version !== ''}
                    autoFocus
                  />
                </div>
              </div>
              {modal.form.version !== '' && formCheck.version && <p className="dv-field-error">{formCheck.version}</p>}
              {formCheck.doublon && <p className="dv-field-error">{formCheck.doublon}</p>}
              <p className="dv-field-hint">
                Écrire le numéro exactement comme le poste le porte (constante ou ressource de l'exe).
              </p>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="dv-date">Publiée le</label>
                  <input
                    id="dv-date"
                    type="date"
                    value={modal.form.publiee_le}
                    onChange={(event) => updateForm({ publiee_le: event.target.value })}
                  />
                  {formCheck.date && <p className="dv-field-error">{formCheck.date}</p>}
                </div>
                <div className="form-group">
                  <label htmlFor="dv-taille">Taille (octets)</label>
                  <input
                    id="dv-taille"
                    inputMode="numeric"
                    placeholder="ex. 81234567"
                    value={modal.form.taille_octets}
                    onChange={(event) => updateForm({ taille_octets: event.target.value })}
                  />
                  {formCheck.taille ? (
                    <p className="dv-field-error">{formCheck.taille}</p>
                  ) : (
                    modal.form.taille_octets.trim() !== '' && (
                      <p className="dv-field-hint">≈ {formatTaille(Number(modal.form.taille_octets.trim()))}</p>
                    )
                  )}
                </div>
              </div>

              <div className="form-group dv-checkbox">
                <input
                  id="dv-obligatoire"
                  type="checkbox"
                  checked={modal.form.obligatoire}
                  onChange={(event) => updateForm({ obligatoire: event.target.checked })}
                />
                <label htmlFor="dv-obligatoire">
                  Mise à jour obligatoire <span className="dv-field-hint">(le poste l'affiche ; rien n'est imposé)</span>
                </label>
              </div>

              <div className="form-group">
                <label htmlFor="dv-notes">Notes de version</label>
                <textarea
                  id="dv-notes"
                  rows={6}
                  placeholder={'Un changement par ligne, texte brut\nex. Déclaration G50 : nouveau barème'}
                  value={modal.form.notes}
                  onChange={(event) => updateForm({ notes: event.target.value })}
                />
                <p className={formCheck.notes ? 'dv-field-error' : 'dv-field-hint'}>
                  {modal.form.notes.length.toLocaleString('fr-FR')} / {NOTES_MAX.toLocaleString('fr-FR')} caractères
                </p>
              </div>

              {modal.erreur && (
                <div className="dv-form-erreur" role="alert">
                  <strong>{modal.erreur.message}</strong>
                  {modal.erreur.detail && <span className="dv-form-erreur-detail">{modal.erreur.detail}</span>}
                </div>
              )}

              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setModal(null)} disabled={saving}>
                  Annuler
                </button>
                <button type="submit" className="btn btn-primary" disabled={!formCheck.valid || saving}>
                  {saving ? 'Enregistrement…' : modal.mode === 'create' ? 'Inscrire' : 'Enregistrer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {nettoyage && (
        <div className="modal-overlay" role="presentation">
          <div className="modal dv-modal-lot" role="dialog" aria-modal="true" aria-labelledby="dv-nettoyage-titre">
            <h2 id="dv-nettoyage-titre">Nettoyer le catalogue et le dépôt</h2>
            <p className="dv-info">
              Partent, pour chaque produit, les versions que <strong>les postes ne voient pas</strong> :
              celles dont le numéro ne dépasse pas la version vue des postes, publiées ou non. Une
              version inscrite plus récente est en préparation : elle reste. Partent aussi leurs lots
              et porteurs du dépôt, et les fichiers rattachés à aucune version déposés il y a plus de
              24 h. Un fichier rattaché à une version gardée, ou cité dans ses notes, reste.
            </p>
            <div className="form-group">
              <label htmlFor="dv-nettoyage-produit">Produit</label>
              <select
                id="dv-nettoyage-produit"
                value={nettoyage.produit}
                disabled={nettoyage.chargement || saving}
                onChange={(event) => { void chargerNettoyage(event.target.value); }}
              >
                <option value="">Tous les produits</option>
                {DESKTOP_PRODUITS.map((produit) => (
                  <option key={produit.code} value={produit.code}>{produit.nom}</option>
                ))}
              </select>
            </div>
            {nettoyage.chargement ? (
              <p className="dv-help">Calcul de ce qui partirait…</p>
            ) : nettoyage.versions.length === 0 && nettoyage.fichiers.length === 0 ? (
              <p className="dv-help">Rien à nettoyer : le catalogue et le dépôt sont déjà propres.</p>
            ) : (
              <>
                <h3>Versions supprimées ({nettoyage.versions.length})</h3>
                {nettoyage.versions.length === 0 ? (
                  <p className="dv-field-hint">Aucune.</p>
                ) : (
                  <ul className="dv-nettoyage-liste">
                    {nettoyage.versions.map((v) => (
                      <li key={v.id}>
                        {nomProduit(v.produit)} <span className="dv-mono">{v.version}</span>
                        {v.publiee ? ' — publiée, remplacée par une plus récente' : ' — jamais publiée'}
                      </li>
                    ))}
                  </ul>
                )}
                <h3>
                  Fichiers effacés du dépôt ({nettoyage.fichiers.length}, {formatTaille(nettoyage.octets)})
                </h3>
                {nettoyage.fichiers.length === 0 ? (
                  <p className="dv-field-hint">Aucun.</p>
                ) : (
                  <ul className="dv-nettoyage-liste">
                    {nettoyage.fichiers.map((f) => (
                      <li key={`${f.produit}/${f.nom}`}>
                        <span className="dv-mono">{f.nom}</span> · {formatTaille(f.taille_octets)} · {f.raison}
                      </li>
                    ))}
                  </ul>
                )}
                {nettoyage.versions.some((v) => v.publiee) && !canPublish && (
                  <p className="dv-field-error">
                    Des versions publiées partiraient : il faut aussi le droit de publication.
                  </p>
                )}
              </>
            )}
            <div className="modal-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setNettoyage(null)} disabled={saving}>
                Fermer
              </button>
              <button
                type="button"
                className="btn btn-secondary dv-btn-danger"
                disabled={
                  saving ||
                  nettoyage.chargement ||
                  (nettoyage.versions.length === 0 && nettoyage.fichiers.length === 0) ||
                  (nettoyage.versions.some((v) => v.publiee) && !canPublish)
                }
                onClick={() => executerNettoyage(nettoyage)}
              >
                {saving ? 'Suppression…' : 'Supprimer définitivement'}
              </button>
            </div>
          </div>
        </div>
      )}

      {lotModal && (
        <div className="modal-overlay" role="presentation">
          <div className="modal dv-modal-lot" role="dialog" aria-modal="true" aria-labelledby="dv-lot-titre">
            <h2 id="dv-lot-titre">
              Lot de {nomProduit(lotModal.item.produit)} {lotModal.item.version}
            </h2>
            <p className="dv-info">
              Le fichier se dépose <strong>sur le serveur</strong> (scp, dossier du produit) ; on le
              choisit ici. La taille et l'empreinte SHA-256 sont <strong>calculées par le serveur</strong> sur
              le fichier déposé — à comparer au <code>publication.txt</code> de la compilation.
            </p>
            {lotModal.item.publiee && (
              <p className="dv-info dv-info-publiee">
                Cette version est <strong>publiée</strong> : le rattachement change dès l'enregistrement
                ce que reçoivent les postes dont le contrat est en cours.
              </p>
            )}

            {lotModal.item.lot_fichier && (
              <div className={`dv-lot-actuel ${lotModal.item.lot_fichier_ok === false ? 'dv-lot-actuel-absent' : ''}`}>
                <h3>Lot rattaché</h3>
                <dl className="dv-lot-dl">
                  <dt>Fichier</dt>
                  <dd className="dv-mono">{lotModal.item.lot_fichier}</dd>
                  <dt>Taille</dt>
                  <dd>
                    {formatTaille(lotModal.item.lot_taille_octets)}{' '}
                    <span className="dv-field-hint">
                      ({lotModal.item.lot_taille_octets?.toLocaleString('fr-FR')} octets)
                    </span>
                  </dd>
                  <dt>SHA-256</dt>
                  <dd className="dv-mono dv-lot-sha">{lotModal.item.lot_sha256}</dd>
                  <dt>Adresse</dt>
                  <dd className="dv-mono">{lotModal.item.lot_url}</dd>
                  {lotModal.item.porteur_fichier && (
                    <>
                      <dt>Porteur</dt>
                      <dd className="dv-mono">
                        {lotModal.item.porteur_url}
                        {lotModal.item.porteur_fichier_ok === false && (
                          <span className="dv-lot-absent"> — fichier absent du dépôt</span>
                        )}
                      </dd>
                    </>
                  )}
                </dl>
                {lotModal.item.lot_fichier_ok === false && (
                  <p className="dv-field-error">
                    Le fichier manque du dépôt, ou n'a plus la taille enregistrée : aucun poste ne
                    reçoit d'adresse, et publier cette version est refusé. Redéposer le fichier, ou
                    détacher le lot.
                  </p>
                )}
                <p className="dv-field-hint">
                  L'adresse du <strong>porteur</strong> ne sert qu'à être recopiée dans les notes de
                  version : c'est l'outil du premier saut des postes en 1.0.0.0, qui ne savent pas
                  encore se mettre à jour seuls.
                </p>
              </div>
            )}

            {lotModal.chargement ? (
              <p className="dv-help">Lecture du dépôt…</p>
            ) : (
              <>
                <div className="form-group">
                  <label htmlFor="dv-lot-fichier">Lot déposé (.zip)</label>
                  <select
                    id="dv-lot-fichier"
                    value={lotModal.choixLot}
                    onChange={(event) =>
                      setLotModal((current) => (current ? { ...current, choixLot: event.target.value } : current))
                    }
                  >
                    <option value="">— choisir un fichier du dépôt —</option>
                    {lotModal.lots.map((fichier) => (
                      <option key={fichier.nom} value={fichier.nom}>
                        {fichier.nom} · {formatTaille(fichier.taille_octets)} · déposé le {fichier.depose_le}
                        {fichier.rattache_a ? ` · déjà rattaché à ${fichier.rattache_a}` : ''}
                      </option>
                    ))}
                  </select>
                  {lotModal.lots.length === 0 && (
                    <p className="dv-field-hint">
                      Le dépôt de {nomProduit(lotModal.item.produit)} est vide : déposer d'abord le
                      fichier sur le serveur (voir docs/publier-version-poste.md).
                    </p>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="dv-lot-porteur">Porteur du premier saut (.exe, facultatif)</label>
                  <select
                    id="dv-lot-porteur"
                    value={lotModal.choixPorteur}
                    onChange={(event) =>
                      setLotModal((current) => (current ? { ...current, choixPorteur: event.target.value } : current))
                    }
                  >
                    <option value="">— aucun —</option>
                    {lotModal.porteurs.map((fichier) => (
                      <option key={fichier.nom} value={fichier.nom}>
                        {fichier.nom} · {formatTaille(fichier.taille_octets)} · déposé le {fichier.depose_le}
                        {fichier.rattache_a ? ` · déjà rattaché à ${fichier.rattache_a}` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              </>
            )}

            <div className="modal-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setLotModal(null)} disabled={saving}>
                Fermer
              </button>
              {lotModal.item.lot_fichier && (
                <button
                  type="button"
                  className="btn btn-secondary dv-btn-danger"
                  disabled={saving}
                  onClick={() => detacherLot(lotModal.item)}
                >
                  Détacher le lot
                </button>
              )}
              <button
                type="button"
                className="btn btn-primary"
                disabled={saving || lotModal.chargement || lotModal.choixLot === ''}
                onClick={() => { void rattacherLot(lotModal); }}
              >
                {saving ? 'Calcul de l’empreinte…' : 'Rattacher ce lot'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DesktopVersions;
